import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { TreeNode } from '../../model/tree-node';
import { TreeItemBadgeComponent } from '../tree-item-badge/tree-item-badge.component';
import { TreeComponent } from '../tree/tree.component';

@Component({
  selector: 'app-tree-item',
  templateUrl: './tree-item.component.html',
  styleUrls: [ './tree-item.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ TreeItemBadgeComponent ]
})
export class TreeItemComponent implements OnChanges {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private treeComponent = inject(TreeComponent);

  @Input() item: TreeNode;
  @Input() level = 0;
  @Input() selectedItems: string[] = [];
  @Input() showCount = true;
  @Output() selectItem = new EventEmitter<TreeNode>();
  @Output() deselectItem = new EventEmitter<TreeNode>();
  selected = false;
  collapsed = true;
  hasSelectedDescendant = false;

  handleSelectionChange(current: TreeNode) {
    if (current.id !== this.item.id) {
      this.selected = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  ngOnChanges({ selectedItems }: SimpleChanges) {
    this.collapsed = this.treeComponent.collapsedNodes.has(this.item.id)
      ? this.treeComponent.collapsedNodes.get(this.item.id)
      : true;

    // set the state to selected if already selected
    if (selectedItems) {
      this.selected = selectedItems.currentValue.includes(this.item.id);

      // if any children are selected, expand this section
      for (const itemId of selectedItems.currentValue) {
        if (this.findDescendant(this.item, itemId)) {
          this.hasSelectedDescendant = true;
          this.collapsed = false;
          break;
        }
      }
    }
  }

  /**
   * Selects or deselect the current node
   */
  toggle() {
    if (!this.selected) {
      this.selectItem.emit(this.item);
    } else {
      this.deselectItem.emit(this.item);
    }
    this.selected = !this.selected;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Finds an item anywhere in the tree
   *
   * @param parent - The root of a tree, or a subtree parent
   * @param itemId - the node id we are searching for
   * @return
   */
  findDescendant(parent: TreeNode, itemId: string): TreeNode | null {
    for (const item of parent.items) {
      let descendant: TreeNode;

      if (item.id === itemId) {
        descendant = item;
      } else {
        descendant = this.findDescendant(item, itemId);
      }

      if (descendant) {
        return descendant;
      }
    }

    return null;
  }

  hasVisibleItems(): boolean {
    return this.item.items.some(item => !item.hidden && !item.deleted);
  }

  toggleCollapsed() {
    this.collapsed = !this.collapsed;
    this.treeComponent.collapsedNodes.set(this.item.id, this.collapsed);
  }
}
